import React from 'react';
import { createPortal } from 'react-dom';
import { useEscapeLayer } from '@/shared/hooks/useEscapeLayer';
import type { Handler } from '../../api/handlersApi';
import { resolveHandlerRef } from '../../utils/handlerRefs';
import type { StepNode } from './types';
import { formatJson, safeParseJson } from './layout';
import { useAiText } from '@/features/system/ai/hooks/useAiText';
import { buildWorkflowIoAiUserPrompt, WORKFLOW_IO_AI_SYSTEM_PROMPT } from '../../ai/workflowIoAiPrompt';

type JsonSchemaType = 'integer' | 'floatpoint' | 'string' | 'boolean' | 'jsonb' | 'timestamp';

function normalizeValueType(raw: string): JsonSchemaType {
  const t = raw.trim().toLowerCase();
  if (!t) return 'string';

  const noPrefix = t.startsWith('v_') ? t.slice(2) : t;
  switch (noPrefix) {
    case 'int8':
    case 'integer':
      return 'integer';
    case 'float8':
    case 'number':
    case 'floatpoint':
      return 'floatpoint';
    case 'text':
    case 'string':
      return 'string';
    case 'bool':
    case 'boolean':
      return 'boolean';
    case 'jsonb':
    case 'object':
    case 'array':
      return 'jsonb';
    case 'timestamptz':
    case 'timestamp':
      return 'timestamp';
    default:
      return 'string';
  }
}

interface Props {
  workflowKey?: string;
  workflowDescription?: string;
  inputsSchemaJson: string;
  outputsSchemaJson: string;
  outputsBindingsJson: string;
  nodes: StepNode[];
  handlers: Handler[];
  idempotency?: {
    enabled: boolean;
    scopeKeyFields: string[];
    onChangeScopeKeyFields: (next: string[]) => void;
  };
  onChangeWorkflowKey?: (value: string) => void;
  onChangeWorkflowDescription?: (value: string) => void;
  onChangeInputsSchemaJson: (value: string) => void;
  onChangeOutputsSchemaJson: (value: string) => void;
  onChangeOutputsBindingsJson: (value: string) => void;

	// Optional: allow parent to clean up bindings when an input key is deleted.
	// This prevents the input from being auto-reintroduced by binding-derived inference.
	onRemoveInputKey?: (key: string) => void;
}

type AiAssistProps = {
  workflowKey?: string;
  workflowDescription?: string;
  nodes: StepNode[];
  isUpdateMode?: boolean;
  onChangeWorkflowKey?: (value: string) => void;
  onChangeWorkflowDescription?: (value: string) => void;
  onChangeInputsSchemaJson: (value: string) => void;
  onChangeOutputsSchemaJson: (value: string) => void;
  onChangeOutputsBindingsJson: (value: string) => void;
};

type AiIoPayload = {
  inputs_schema: unknown;
  outputs_schema: unknown;
  outputs_bindings: unknown;
  workflow_key?: unknown;
  workflow_description?: unknown;
};

function isAiIoPayload(value: unknown): value is AiIoPayload {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return ('inputs_schema' in v) && ('outputs_schema' in v) && ('outputs_bindings' in v);
}

function safeJsonParse(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) throw new Error('AI response is empty.');
  try {
    return JSON.parse(trimmed);
  } catch {
    throw new Error('AI response is not valid JSON. Ensure the prompt forces JSON-only output.');
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function validateJsonSchemaObject(schemaName: string, schema: unknown): string[] {
  const errs: string[] = [];
  if (!isPlainObject(schema)) {
    errs.push(`${schemaName} must be an object (JSON Schema).`);
    return errs;
  }

  const t = schema.type;
  if (t !== undefined && t !== 'object') {
    errs.push(`${schemaName}.type should be "object" (got ${JSON.stringify(t)}).`);
  }

  const props = schema.properties;
  if (props !== undefined && !isPlainObject(props)) {
    errs.push(`${schemaName}.properties must be an object when present.`);
  }

  const req = schema.required;
  if (req !== undefined) {
    if (!Array.isArray(req) || req.some((x) => typeof x !== 'string')) {
      errs.push(`${schemaName}.required must be an array of strings when present.`);
    }
  }

  return errs;
}

function validateOutputsBindings(bindings: unknown): string[] {
  const errs: string[] = [];
  if (!isPlainObject(bindings)) {
    errs.push('outputs_bindings must be an object mapping output keys to binding strings.');
    return errs;
  }

  const bindingPattern = /^\{\{(inputs\.[\w-]+|steps\.[\w-]+\.outputs\.[\w-]+)\}\}$/;
  for (const [key, value] of Object.entries(bindings)) {
    if (typeof value !== 'string') {
      errs.push(`outputs_bindings.${key} must be a string binding.`);
      continue;
    }
    if (!bindingPattern.test(value.trim())) {
      errs.push(
        `outputs_bindings.${key} must look like {{inputs.<key>}} or {{steps.<step_id>.outputs.<key>}} (got ${JSON.stringify(value)}).`
      );
    }
  }
  return errs;
}

function coerceObject(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {};
}

function parseJsonObject(text: string): Record<string, any> {
  try {
    const v = safeParseJson(text);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, any>) : {};
  } catch {
    return {};
  }
}

function normalizeSchemaObject(raw: Record<string, any>): Record<string, any> {
  const next = { ...raw };
  next.$schema = next.$schema || 'https://json-schema.org/draft/2020-12/schema';
  next.type = next.type || 'object';
  next.additionalProperties = typeof next.additionalProperties === 'boolean' ? next.additionalProperties : true;
  next.properties = coerceObject(next.properties);
  next.required = Array.isArray(next.required) ? next.required.filter((x) => typeof x === 'string') : [];

  for (const [key, value] of Object.entries(next.properties)) {
    const prop = coerceObject(value);
    if (typeof prop.type === 'string') {
      next.properties[key] = { ...prop, type: normalizeValueType(prop.type) };
    }
  }

  return next;
}

function nextKey(base: string, existing: Set<string>): string {
  if (!existing.has(base)) return base;
  let i = 2;
  while (existing.has(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}

function getSchemaRow(schema: Record<string, any>, key: string): { type: JsonSchemaType; description: string } {
  const prop = coerceObject(schema.properties?.[key]);
  const type = normalizeValueType(typeof prop.type === 'string' ? prop.type : 'string');
  const description = typeof prop.description === 'string' ? prop.description : '';
  return { type, description };
}

function setSchemaRow(schema: Record<string, any>, key: string, patch: { type?: JsonSchemaType; description?: string }) {
  const prev = coerceObject(schema.properties?.[key]);
  schema.properties[key] = {
    ...prev,
    ...(patch.type ? { type: normalizeValueType(patch.type) } : {}),
    ...(typeof patch.description === 'string' ? { description: patch.description } : {}),
  };
}

function parseOutputsBinding(value: unknown): { stepId: string; outputKey: string; raw: string } {
  if (typeof value !== 'string') return { stepId: '', outputKey: '', raw: '' };
  const raw = value;
  const m = raw.trim().match(/^\{\{\s*steps\.([a-zA-Z0-9_-]+)\.outputs\.([a-zA-Z0-9_-]+)\s*\}\}$/);
  if (!m) return { stepId: '', outputKey: '', raw };
  return { stepId: m[1], outputKey: m[2], raw };
}

function listHandlerOutputs(handlerConfig: unknown): string[] {
  const cfg = coerceObject(handlerConfig);
  const outputsSchema = coerceObject(cfg.outputs_schema);
  const props = coerceObject(outputsSchema.properties);
  const schemaKeys = Object.keys(props);
  if (schemaKeys.length > 0) return schemaKeys;
  const outputsList = Array.isArray(cfg.outputs) ? cfg.outputs.filter((x) => typeof x === 'string') : [];
  return outputsList;
}

export function IoAiAssistCard({
  workflowKey,
  workflowDescription,
  nodes,
  isUpdateMode = false,
  onChangeWorkflowKey,
  onChangeWorkflowDescription,
  onChangeInputsSchemaJson,
  onChangeOutputsSchemaJson,
  onChangeOutputsBindingsJson,
}: AiAssistProps) {
  const aiText = useAiText();
  const [aiOpen, setAiOpen] = React.useState(false);
  const [aiSpec, setAiSpec] = React.useState('');
  const [aiBusy, setAiBusy] = React.useState(false);
  const [aiError, setAiError] = React.useState<string | null>(null);
  useEscapeLayer(() => { setAiOpen(false); setAiError(null); }, aiOpen && !aiBusy);

  const handleFillIoWithAi = async () => {
    setAiError(null);
    const spec = aiSpec.trim();
    if (!spec) {
      setAiError('Describe the workflow IO in plain text first.');
      return;
    }

    // Guardrail: this AI assist ONLY edits inputs/outputs schemas + output bindings.
    // Users sometimes ask it to add steps/handlers; that belongs in the DAG editor / step builder.
    const specLower = spec.toLowerCase();
    const looksLikeDagRequest =
      specLower.includes('step') ||
      specLower.includes('steps') ||
      specLower.includes('handler') ||
      specLower.includes('service') ||
      specLower.includes('create_directory') ||
      specLower.includes('pacdaemon') ||
      specLower.includes('wire') ||
      specLower.includes('wiring') ||
      specLower.includes('edge') ||
      specLower.includes('dag');
    if (looksLikeDagRequest) {
      setAiError(
        'This AI assist only generates Inputs/Outputs schemas + Outputs bindings. It cannot add steps/handlers or edges. Go to the Steps tab and click “Macro AI” to add/wire steps using the handler catalog, then come back here to refine IO/bindings if needed.'
      );
      return;
    }

    setAiBusy(true);
    try {
      const systemPrompt = WORKFLOW_IO_AI_SYSTEM_PROMPT;
      const userPrompt = buildWorkflowIoAiUserPrompt(spec, {
        available_step_ids: nodes.map((n) => n.id).filter(Boolean),
        ...(isUpdateMode ? { current_workflow_key: workflowKey, current_workflow_description: workflowDescription } : {}),
      });

      const res = await aiText.generate({
        system_prompt: systemPrompt,
        user_prompt: userPrompt,
        temperature: 0.2,
        max_output_tokens: 2048,
      });
      if (!res) return; // 202 async dispatch — result via SSE

      const json = safeJsonParse(res.content);
      if (!isAiIoPayload(json)) {
        throw new Error('AI response JSON does not match expected IO payload (inputs_schema/outputs_schema/outputs_bindings).');
      }

      const validationErrors = [
        ...validateJsonSchemaObject('inputs_schema', json.inputs_schema),
        ...validateJsonSchemaObject('outputs_schema', json.outputs_schema),
        ...validateOutputsBindings(json.outputs_bindings),
      ];
      if (validationErrors.length) {
        throw new Error(`AI response failed validation:\n- ${validationErrors.join('\n- ')}`);
      }

      onChangeInputsSchemaJson(formatJson(json.inputs_schema));
      onChangeOutputsSchemaJson(formatJson(json.outputs_schema));
      onChangeOutputsBindingsJson(formatJson(json.outputs_bindings));

      // Optional metadata
      // Create vs update semantics:
      // - Create: preserve legacy behavior (AI may propose/update workflow_key)
      // - Update: keep workflow_key stable to avoid accidental "new workflow" behavior
      if (!isUpdateMode && typeof json.workflow_key === 'string' && json.workflow_key.trim() && onChangeWorkflowKey) {
        onChangeWorkflowKey(json.workflow_key.trim());
      }
      if (typeof json.workflow_description === 'string' && onChangeWorkflowDescription) {
        onChangeWorkflowDescription(json.workflow_description.trim());
      }

      setAiOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setAiError(msg || 'AI generation failed.');
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <>
      <div className="sticky top-0 z-10 pac-glass-subtle rounded-md border border-neutral-800 p-3 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="text-xs uppercase tracking-wide text-neutral-400">AI assist</div>
              <div
                className={
                  'text-[10px] px-2 py-0.5 rounded border ' +
                  (isUpdateMode
                    ? 'border-indigo-600/40 bg-indigo-950/30 text-indigo-200'
                    : 'border-neutral-700 pac-glass text-neutral-300')
                }
              >
                {isUpdateMode ? 'Update' : 'Create'}
              </div>
            </div>
            <div className="text-[11px] text-neutral-500">
              {isUpdateMode
                ? 'Describe inputs/outputs (names/types) and what the workflow does. In Update mode, AI will not change workflow key.'
                : 'Describe inputs/outputs (names/types) and what the workflow does. In Create mode, AI may suggest workflow key + description.'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setAiError(null);
              setAiOpen(true);
            }}
            className="rounded border border-neutral-700 px-3 py-1 text-xs text-neutral-200 hover:border-neutral-500 hover:text-neutral-50"
          >
            Fill with AI
          </button>
        </div>
      </div>

      {aiOpen && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Fill workflow IO with AI"
        >
          <div className="w-full max-w-3xl rounded-lg border border-neutral-800 bg-black p-4 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-neutral-100">Fill workflow IO with AI</div>
                <div className="text-xs text-neutral-400">
                  Explain in words: how many inputs/outputs, their names/types, and what the workflow does.
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!aiBusy) {
                    setAiOpen(false);
                    setAiError(null);
                  }
                }}
                className="rounded border border-neutral-700 bg-black px-2 py-1 text-xs text-neutral-200 hover:bg-rose-600 hover:border-rose-600 hover:text-white transition-colors disabled:opacity-60"
                disabled={aiBusy}
                title={aiBusy ? 'Generating…' : 'Close'}
              >
                Close
              </button>
            </div>

            {aiError && <p className="mt-3 text-sm text-amber-300">{aiError}</p>}

            <div className="mt-3">
              <label className="text-xs uppercase tracking-wide text-neutral-400">Workflow IO spec (plain text)</label>
              <textarea
                value={aiSpec}
                onChange={(e) => setAiSpec(e.target.value)}
                placeholder={
                  'Example:\n'
                  + '- Workflow computes sqrt(a^2 + b^2)\n'
                  + '- Inputs: a (number) first argument, b (number) second argument\n'
                  + '- Outputs: result (number) the computed magnitude\n'
                }
                className="mt-1 w-full bg-black border border-neutral-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500 min-h-[160px]"
              />
            </div>

            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded border border-neutral-700 bg-black px-3 py-2 text-xs text-neutral-200 hover:border-neutral-600 hover:border-neutral-600 transition-colors disabled:opacity-60"
                disabled={aiBusy}
                onClick={() => {
                  setAiError(null);
                  setAiSpec('');
                }}
              >
                Clear
              </button>
              <button
                type="button"
                className="rounded border border-neutral-700 bg-black px-3 py-2 text-xs text-neutral-300 hover:bg-emerald-600 hover:border-emerald-600 hover:text-white transition-colors disabled:opacity-60"
                onClick={handleFillIoWithAi}
                disabled={aiBusy}
              >
                {aiBusy ? 'Generating…' : 'Generate & fill'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

export default function IoEditorTab({
  workflowKey,
  workflowDescription,
  inputsSchemaJson,
  outputsSchemaJson,
  outputsBindingsJson,
  nodes,
  handlers,
  idempotency,
  onChangeWorkflowKey,
  onChangeWorkflowDescription,
  onChangeInputsSchemaJson,
  onChangeOutputsSchemaJson,
  onChangeOutputsBindingsJson,
	onRemoveInputKey,
}: Props) {
  const inputsSchemaObj = React.useMemo(() => normalizeSchemaObject(parseJsonObject(inputsSchemaJson)), [inputsSchemaJson]);
  const aiText = useAiText();
  const outputsSchemaObj = React.useMemo(() => normalizeSchemaObject(parseJsonObject(outputsSchemaJson)), [outputsSchemaJson]);
  // Normalize legacy types (v_* and json-schema primitives) into PACMaster value types.
  React.useEffect(() => {
    const normalized = formatJson(normalizeSchemaObject(parseJsonObject(inputsSchemaJson)));
    if (normalized !== inputsSchemaJson) onChangeInputsSchemaJson(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsSchemaJson]);

  React.useEffect(() => {
    const normalized = formatJson(normalizeSchemaObject(parseJsonObject(outputsSchemaJson)));
    if (normalized !== outputsSchemaJson) onChangeOutputsSchemaJson(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputsSchemaJson]);
  const outputsBindingsObj = React.useMemo(() => parseJsonObject(outputsBindingsJson), [outputsBindingsJson]);

  const inputKeys = React.useMemo(() => Object.keys(inputsSchemaObj.properties).sort(), [inputsSchemaObj]);

  const outputKeys = React.useMemo(() => {
    const keys = new Set<string>([...Object.keys(outputsSchemaObj.properties), ...Object.keys(outputsBindingsObj)]);
    return Array.from(keys).sort();
  }, [outputsSchemaObj, outputsBindingsObj]);

  const outputsByStepId = React.useMemo(() => {
    const map = new Map<string, string[]>();
    nodes.forEach((n) => {
      const handler = resolveHandlerRef(handlers, n.def.service || '');
      map.set(n.id, listHandlerOutputs(handler?.config));
    });
    return map;
  }, [nodes, handlers]);

  const updateInputsSchema = (mutate: (schema: Record<string, any>) => void) => {
    const schema = normalizeSchemaObject(parseJsonObject(inputsSchemaJson));
    mutate(schema);
    onChangeInputsSchemaJson(formatJson(schema));
  };

  const updateOutputsSchema = (mutate: (schema: Record<string, any>) => void) => {
    const schema = normalizeSchemaObject(parseJsonObject(outputsSchemaJson));
    mutate(schema);
    onChangeOutputsSchemaJson(formatJson(schema));
  };

  const updateOutputsBindings = (mutate: (bindings: Record<string, any>) => void) => {
    const bindings = parseJsonObject(outputsBindingsJson);
    mutate(bindings);
    onChangeOutputsBindingsJson(formatJson(bindings));
  };

  const schemaTypeOptions: JsonSchemaType[] = ['integer', 'floatpoint', 'string', 'boolean', 'jsonb', 'timestamp'];

  const scopeEnabled = Boolean(idempotency?.enabled);
  const scopeSelected = new Set<string>(Array.isArray(idempotency?.scopeKeyFields) ? idempotency!.scopeKeyFields : []);
  const setScopeKeyFields = (next: string[]) => {
    if (!idempotency) return;
    idempotency.onChangeScopeKeyFields(Array.from(new Set(next.filter((x) => typeof x === 'string' && x.trim()))).sort());
  };

  return (
    <div className="space-y-3">
      <div className="pac-glass rounded-md p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-neutral-200">Inputs (runtime variables)</h4>
          <button
            type="button"
            className="px-3 py-1 rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500"
            onClick={() => {
              const existing = new Set(Object.keys(parseJsonObject(inputsSchemaJson).properties || {}));
              const key = nextKey('input', existing);
              updateInputsSchema((schema) => {
                setSchemaRow(schema, key, { type: 'string', description: '' });
              });
            }}
          >
            Add input
          </button>
        </div>

        <div className="grid grid-cols-12 gap-2 text-[11px] text-neutral-400 px-1">
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-4">Description</div>
          <div className="col-span-1">Scope</div>
          <div className="col-span-1">Req</div>
          <div className="col-span-1">Del</div>
        </div>

        {inputKeys.length === 0 && (
          <div className="text-xs text-neutral-500">No inputs yet. Add inputs like a, b, video_url.</div>
        )}

        {inputKeys.map((key) => {
          const requiredSet = new Set<string>(inputsSchemaObj.required);
          const row = getSchemaRow(inputsSchemaObj, key);
          const scopeChecked = scopeSelected.has(key);

          return (
            <div key={key} className="grid grid-cols-12 gap-2 items-start px-1">
              <input
                className="col-span-3 bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                defaultValue={key}
                onBlur={(e) => {
                  const nextName = e.target.value.trim();
                  if (!nextName || nextName === key) return;
                  updateInputsSchema((schema) => {
                    if (schema.properties[nextName]) {
                      // revert by resetting the input value
                      e.target.value = key;
                      return;
                    }
                    schema.properties[nextName] = schema.properties[key];
                    delete schema.properties[key];
                    schema.required = Array.isArray(schema.required) ? schema.required : [];
                    schema.required = schema.required.map((r: string) => (r === key ? nextName : r));
                  });

                  if (idempotency) {
                    const prev = Array.isArray(idempotency.scopeKeyFields) ? idempotency.scopeKeyFields : [];
                    if (prev.includes(key)) {
                      setScopeKeyFields(prev.map((x) => (x === key ? nextName : x)));
                    }
                  }
                }}
              />

              <select
                className="col-span-2 bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                value={row.type}
                onChange={(e) => {
                  updateInputsSchema((schema) => {
                    setSchemaRow(schema, key, { type: e.target.value as JsonSchemaType });
                  });
                }}
              >
                {schemaTypeOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>

              <input
                className="col-span-4 bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                value={row.description}
                onChange={(e) => {
                  const value = e.target.value;
                  updateInputsSchema((schema) => {
                    setSchemaRow(schema, key, { description: value });
                  });
                }}
                placeholder="What is this input?"
              />

              <div className="col-span-1 flex items-center justify-center">
                <input
                  type="checkbox"
                  className="accent-indigo-500"
                  checked={scopeChecked}
                  onChange={(e) => {
                    if (!idempotency) return;
                    const prev = Array.isArray(idempotency.scopeKeyFields) ? idempotency.scopeKeyFields : [];
                    const next = e.target.checked
                      ? [...prev, key]
                      : prev.filter((x) => x !== key);
                    setScopeKeyFields(next);
                  }}
                  title={scopeEnabled ? 'Include this input in workflow-run idempotency key' : 'Selecting this will enable deterministic run scope'}
                />
              </div>

              <div className="col-span-1 flex items-center justify-center">
                <input
                  type="checkbox"
                  className="accent-emerald-500"
                  checked={requiredSet.has(key)}
                  onChange={(e) => {
                    updateInputsSchema((schema) => {
                      const req = new Set<string>(Array.isArray(schema.required) ? schema.required : []);
                      if (e.target.checked) req.add(key);
                      else req.delete(key);
                      schema.required = Array.from(req).sort();
                    });
                  }}
                  title="Required at runtime"
                />
              </div>

              <div className="col-span-1 flex items-center justify-center">
                <button
                  type="button"
                  className="text-neutral-400 hover:text-rose-300"
                  onClick={() => {
					// Let parent clean up any step/output bindings that reference this input.
					onRemoveInputKey?.(key);

					// Always remove from schema + required + scope.
					updateInputsSchema((schema) => {
						delete schema.properties[key];
						const req = new Set<string>(Array.isArray(schema.required) ? schema.required : []);
						req.delete(key);
						schema.required = Array.from(req).sort();
					});

					if (idempotency) {
						const prev = Array.isArray(idempotency.scopeKeyFields) ? idempotency.scopeKeyFields : [];
						if (prev.includes(key)) {
							setScopeKeyFields(prev.filter((x) => x !== key));
						}
					}
                  }}
                  title="Delete input"
                >
                  ×
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="pac-glass rounded-md p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-neutral-200">Outputs (workflow contract)</h4>
          <button
            type="button"
            className="px-3 py-1 rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500"
            onClick={() => {
              const existing = new Set(outputKeys);
              const key = nextKey('result', existing);
              updateOutputsSchema((schema) => {
                setSchemaRow(schema, key, { type: 'string', description: '' });
                const req = new Set<string>(Array.isArray(schema.required) ? schema.required : []);
                req.add(key);
                schema.required = Array.from(req).sort();
              });
              updateOutputsBindings((bindings) => {
                if (!Object.prototype.hasOwnProperty.call(bindings, key)) {
                  bindings[key] = '';
                }
              });
            }}
          >
            Add output
          </button>
        </div>

        <div className="grid grid-cols-12 gap-2 text-[11px] text-neutral-400 px-1">
          <div className="col-span-2">Name</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-3">Description</div>
          <div className="col-span-2">From step</div>
          <div className="col-span-2">Output key</div>
          <div className="col-span-1">Del</div>
        </div>

        {outputKeys.length === 0 && (
          <div className="text-xs text-neutral-500">No workflow outputs yet. Add an output like result, then bind it to a step output.</div>
        )}

        {outputKeys.map((outKey) => {
          const row = getSchemaRow(outputsSchemaObj, outKey);
          const binding = parseOutputsBinding(outputsBindingsObj[outKey]);
          const stepId = binding.stepId;
          const outputKey = binding.outputKey;
          const outputOptions = stepId ? (outputsByStepId.get(stepId) || []) : [];

          return (
            <div key={outKey} className="grid grid-cols-12 gap-2 items-start px-1">
              <input
                className="col-span-2 bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                defaultValue={outKey}
                onBlur={(e) => {
                  const nextName = e.target.value.trim();
                  if (!nextName || nextName === outKey) return;

                  updateOutputsSchema((schema) => {
                    if (schema.properties[nextName]) {
                      e.target.value = outKey;
                      return;
                    }
                    schema.properties[nextName] = schema.properties[outKey];
                    delete schema.properties[outKey];
                    schema.required = Array.isArray(schema.required) ? schema.required : [];
                    schema.required = schema.required.map((r: string) => (r === outKey ? nextName : r));
                  });

                  updateOutputsBindings((bindings) => {
                    if (Object.prototype.hasOwnProperty.call(bindings, outKey)) {
                      bindings[nextName] = bindings[outKey];
                      delete bindings[outKey];
                    }
                  });
                }}
              />

              <select
                className="col-span-2 bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                value={row.type}
                onChange={(e) => {
                  updateOutputsSchema((schema) => {
                    setSchemaRow(schema, outKey, { type: e.target.value as JsonSchemaType });
                  });
                }}
              >
                {schemaTypeOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>

              <input
                className="col-span-3 bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                value={row.description}
                onChange={(e) => {
                  const value = e.target.value;
                  updateOutputsSchema((schema) => {
                    setSchemaRow(schema, outKey, { description: value });
                  });
                }}
                placeholder="What does the workflow return?"
              />

              <select
                className="col-span-2 bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                value={stepId}
                onChange={(e) => {
                  const nextStep = e.target.value;
                  const options = outputsByStepId.get(nextStep) || [];
                  const nextOutput = options[0] || '';
                  updateOutputsBindings((bindings) => {
                    bindings[outKey] = nextStep && nextOutput ? `{{steps.${nextStep}.outputs.${nextOutput}}}` : '';
                  });
                }}
              >
                <option value="">— step —</option>
                {nodes.map((n) => (
                  <option key={n.id} value={n.id}>{n.id}</option>
                ))}
              </select>

              <select
                className="col-span-2 bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                value={outputKey}
                onChange={(e) => {
                  const nextOutput = e.target.value;
                  if (!stepId || !nextOutput) return;
                  updateOutputsBindings((bindings) => {
                    bindings[outKey] = `{{steps.${stepId}.outputs.${nextOutput}}}`;
                  });
                }}
                disabled={!stepId}
              >
                <option value="">— output —</option>
                {outputOptions.map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>

              <div className="col-span-1 flex items-center justify-center">
                <button
                  type="button"
                  className="text-neutral-400 hover:text-rose-300"
                  onClick={() => {
                    updateOutputsSchema((schema) => {
                      delete schema.properties[outKey];
                      const req = new Set<string>(Array.isArray(schema.required) ? schema.required : []);
                      req.delete(outKey);
                      schema.required = Array.from(req).sort();
                    });
                    updateOutputsBindings((bindings) => {
                      delete bindings[outKey];
                    });
                  }}
                  title="Delete output"
                >
                  ×
                </button>
              </div>

              {typeof outputsBindingsObj[outKey] === 'string' && outputsBindingsObj[outKey] && !binding.stepId && (
                <div className="col-span-12 text-[10px] text-amber-300 px-1">
                  {'This output binding is not in the standard form {{steps.<id>.outputs.<key>}}. Use JSON tab to edit raw.'}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-[11px] text-neutral-500">
        {'Best practice: use Inputs as runtime variables (A, B, video_url) and Outputs to expose final results. Step-to-step wiring stays inside step params via {{steps.*.outputs.*}}.'}
      </div>
    </div>
  );
}
